import { UTCDate } from '@date-fns/utc';
import { addMonths, endOfMonth, format, getYear, startOfMonth, subMonths } from 'date-fns';
import { AccountView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getEstimatedRunwayData } from './get-estimated-runway-data';
import { getHistoricalCashBalance } from './get-historical-cash-balance';
import { getMonthlyBurnData } from './get-monthly-burn-data';

export type RunwayDataPoint = {
  period: string; // Display label (e.g., "Jan", "Today", "Mar 25")
  balance: number; // Cash balance at this point in time
  monthsElapsed: number; // Months from the base date (negative for past, positive for future)
  isProjected: boolean; // true for future projections, false for historical data
  burnAmount?: number; // Monthly burn rate at this point
  runwayMonths?: number; // Runway in months from this point
  date: UTCDate; // Actual date for this data point
};

// EPSILON is a tiny threshold to treat near-zero burn as zero. Floating-point
// math can yield values like 0.0000004 instead of 0, which would make the
// runway calculation (division by burn) explode to huge numbers and produce
// misleading projections. If |netMonthlyCashFlow| < EPSILON we consider burn
// effectively zero and render steady, infinite runway points.
const EPSILON = 0.000001;

const createPeriodLabel = (date: UTCDate, baseYear: number, isToday: boolean = false): string => {
  if (isToday) return 'Today';
  const pointYear = getYear(date);
  return pointYear === baseYear ? format(date, 'MMM') : format(date, "MMM ''yy");
};

/**
 * Helper function to calculate data for a specific historical or current point
 */
const calculateDataPoint = (
  accounts: AccountView[],
  baseCurrency: SupportedCountryCurrency,
  pointDate: UTCDate,
  monthsFromBase: number,
  baseYear: number,
  isProjected: boolean = false,
  isToday: boolean = false,
): RunwayDataPoint => {
  const periodStart = startOfMonth(pointDate);
  const periodEnd = isToday ? pointDate : endOfMonth(pointDate);

  const prevPeriodStart = startOfMonth(subMonths(pointDate, 1));
  const prevPeriodEnd = endOfMonth(subMonths(pointDate, 1));

  const cashData = getHistoricalCashBalance({
    accounts,
    baseCurrency,
    targetDate: pointDate,
  });

  // For all points, use the previous full calendar month's burn
  const lastFullMonthStart = startOfMonth(subMonths(pointDate, 1));
  const lastFullMonthEnd = endOfMonth(subMonths(pointDate, 1));
  const beforeLastFullMonthStart = startOfMonth(subMonths(pointDate, 2));
  const beforeLastFullMonthEnd = endOfMonth(subMonths(pointDate, 2));

  const burnData = getMonthlyBurnData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: lastFullMonthStart,
    currentPeriodEndDate: lastFullMonthEnd,
    previousPeriodStartDate: beforeLastFullMonthStart,
    previousPeriodEndDate: beforeLastFullMonthEnd,
  });

  const runwayAtPoint = getEstimatedRunwayData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: periodStart,
    currentPeriodEndDate: periodEnd,
    previousMonthStartDate: prevPeriodStart,
    previousMonthEndDate: prevPeriodEnd,
  });

  return {
    period: createPeriodLabel(pointDate, baseYear, isToday),
    balance: cashData.historicalBalanceInBaseCurrency || 0,
    monthsElapsed: monthsFromBase,
    isProjected,
    burnAmount: Math.abs(burnData.monthlyBurnInBaseCurrency || 0),
    runwayMonths: runwayAtPoint.currentRunwayInMonths,
    date: new UTCDate(pointDate),
  };
};

/**
 * Generates runway chart data with historical context and future projections
 *
 * Creates a 7-point chart:
 * - 3 historical data points showing past performance
 * - 1 current "Today" point
 * - 3 future projection points based on current burn rate
 *
 * Spacing between points is fixed at 1 month so dates fall on the same
 * day-of-month relative to today.
 */
export const getRunwayChartData = (
  accounts: AccountView[],
  baseCurrency: SupportedCountryCurrency,
  baseDate: UTCDate = new UTCDate(), // Base date for calculations (defaults to today)
): RunwayDataPoint[] => {
  // Configuration: number of data points before and after today
  const segmentsBeforeToday = 3;
  const segmentsAfterToday = 3;

  const data: RunwayDataPoint[] = [];
  const currentYear = getYear(baseDate);

  // === STEP 1: Establish reference months ===
  const previousMonth = startOfMonth(subMonths(baseDate, 1));
  const endOfPreviousMonth = endOfMonth(subMonths(baseDate, 1));

  // Use fixed 1-month spacing

  // Points are exactly one month apart (e.g., Sep 11 → Aug 11 / Oct 11)
  const monthsPerSegmentFuture = 1;

  // === STEP 2: Historical data points ===
  for (let i = -segmentsBeforeToday; i < 0; i++) {
    const monthsFromToday = i * monthsPerSegmentFuture;
    const pointDate = addMonths(baseDate, monthsFromToday);

    const historicalPoint = calculateDataPoint(
      accounts,
      baseCurrency,
      pointDate,
      monthsFromToday,
      currentYear,
      false, // isProjected = false for historical data
    );

    data.push(historicalPoint);
  }

  // Get previous full month's burn data for tooltips and future projections
  const previousMonthBurnData = getMonthlyBurnData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: previousMonth,
    currentPeriodEndDate: endOfPreviousMonth,
    previousPeriodStartDate: startOfMonth(subMonths(previousMonth, 1)),
    previousPeriodEndDate: endOfMonth(subMonths(previousMonth, 1)),
  });

  // === STEP 3: Current "Today" point ===
  const todayPoint = calculateDataPoint(
    accounts,
    baseCurrency,
    baseDate,
    0, // monthsElapsed = 0 for today
    currentYear,
    false, // isProjected = false for current data
    true, // isToday = true for special handling
  );

  // Override burn amount for Today with the last full month's burn
  const todayPointWithLastMonthBurn: RunwayDataPoint = {
    ...todayPoint,
    burnAmount: Math.abs(previousMonthBurnData.monthlyBurnInBaseCurrency || 0),
  };

  data.push(todayPointWithLastMonthBurn);

  const currentCashData = getHistoricalCashBalance({
    accounts,
    baseCurrency,
    targetDate: baseDate,
  });

  // === STEP 4: Future projection points ===
  const netMonthlyCashFlow = -Math.abs(previousMonthBurnData.monthlyBurnInBaseCurrency || 0); // Negative for burn
  const currentBalance = currentCashData.historicalBalanceInBaseCurrency || 0;

  // If burn is effectively zero, runway is infinite. Show a few steady points and return.
  if (Math.abs(netMonthlyCashFlow) < EPSILON) {
    for (let i = 1; i <= segmentsAfterToday; i++) {
      const monthsFromToday = i * monthsPerSegmentFuture;
      const pointDate = addMonths(baseDate, monthsFromToday);
      const label = createPeriodLabel(pointDate, currentYear);
      data.push({
        period: label,
        balance: currentBalance,
        monthsElapsed: monthsFromToday,
        isProjected: true,
        burnAmount: Math.abs(previousMonthBurnData.monthlyBurnInBaseCurrency || 0),
        runwayMonths: Infinity,
        date: new UTCDate(pointDate),
      });
    }
    return data;
  }

  // Otherwise, create points until the projected balance reaches zero
  const safetyCap = 240; // Prevent unbounded loops
  for (let i = 1; i <= safetyCap; i++) {
    const monthsFromToday = i * monthsPerSegmentFuture;
    const pointDate = addMonths(baseDate, monthsFromToday);

    // Project balance forward using current burn rate
    // Formula: future_balance = current_balance + (months * monthly_cash_flow)
    const projectedBalance = Math.max(0, currentBalance + monthsFromToday * netMonthlyCashFlow);

    // Calculate remaining runway from this projected point
    // If balance reaches 0, runway becomes 0; if no burn, runway is infinite
    const remainingRunway = projectedBalance / Math.abs(netMonthlyCashFlow);

    // Create display label for this future point
    const label = createPeriodLabel(pointDate, currentYear);

    data.push({
      period: label,
      balance: projectedBalance,
      monthsElapsed: monthsFromToday,
      isProjected: true, // Mark as projection
      burnAmount: Math.abs(previousMonthBurnData.monthlyBurnInBaseCurrency || 0), // Use previous month's burn rate
      runwayMonths: Math.max(0, remainingRunway),
      date: new UTCDate(pointDate),
    });

    if (projectedBalance <= 0) break;
  }

  return data;
};
